<template>
  <form
    v-on:submit.prevent="onSubmit"
    v-on:reset="emitCloseForm"
    class="form-employee-break-edit">
    <div class="form-employee-break-edit__employee">{{employee.name}} {{employee.positions}}</div>
    <div
      class="form-employee-break-edit__datetime"
      v-if="hasEditDateTime">
      <date-picker-dialog
        v-bind:value="dateAt"
        v-on:input="onInputDateAt"
        v-bind:allowed-dates="checkDayIsWork"
        v-on:change-month="onChangeMonth">
        <template v-slot:trigger="{ listenerObject }">
          <button
            v-if="hasEditDateTime"
            class="form-employee-break-edit__datetime__value"
            type="button"
            v-on="listenerObject">{{ dateAtTitle }}</button>
          <time
            v-else
            v-bind:datetime="date">{{dateAtTitle}}</time>
        </template>
      </date-picker-dialog>
      <span class="form-employee-break-edit__datetime__value">
        <popover-menu>
          <template v-slot:trigger="{ listenerObject }">
            <div
              v-on="listenerObject"
              v-if="startsTime || endsTime">
              <time
                class="form-employee-break-edit__datetime__value"
                v-bind:datetime="startsTime">{{ startsTime }}</time>
              -
              <time
                class="form-employee-break-edit__datetime__value"
                v-bind:datetime="endsTime">{{ endsTime }}</time>
            </div>
            <div
              v-on="listenerObject"
              v-else>Задайте время</div>
          </template>
          <form-appointment-time-range
            v-bind:initial-from="startsTime"
            v-bind:initial-to="endsTime"
            v-bind:preset-from-timeline="timeline.from"
            v-bind:preset-to-timeline="timeline.to"
            v-bind:timeline="[]"
            v-on:submit="onSubmitTime"
          />
        </popover-menu>
      </span>
      <span>
        <div
          class="form-employee-break-edit__datetime__error"
          v-for="(text, index) in dateInputErrors"
          v-bind:key="index">
          {{text}}
        </div>
      </span>
      <span>
        <div
          class="form-employee-break-edit__datetime__error"
          v-for="(text, index) in timeInputErrors"
          v-bind:key="index">
          {{text}}
        </div>
      </span>
    </div>
    <div
      class="form-employee-break-edit__datetime"
      v-else>
      <time
        class="form-employee-break-edit__datetime__value disabled"
        v-bind:datetime="dateAt">{{ dateAtTitle }}</time>
      <div>
        <time
          class="form-employee-break-edit__datetime__value disabled"
          v-bind:datetime="startsTime">{{ startsTime }}</time>
        -
        <time
          class="form-employee-break-edit__datetime__value disabled"
          v-bind:datetime="endsTime">{{ endsTime }}</time>
      </div>
    </div>
    <textarea-length-limitation
      v-model="note"
      title="Примечание"
      v-bind:max-length="100"
      rows="2"
      placeholder="Примечание" />
    <div class="form-employee-break-edit__buttons">
      <button
        type="submit"
        class="form-employee-break-edit__buttons-submit">Сохранить</button>
      <button
        class="form-employee-break-edit__buttons-cancel"
        type="button"
        v-on:click="emitCloseForm">Отмена</button>
      <button
        v-if="hasEditDateTime"
        type="button"
        v-on:click="emitCancelBreak"
        class="form-employee-break-edit__buttons-cancel break">Отменить перерыв</button>
    </div>
  </form>
</template>

<script>
import dateMixin from '@/mixins/dateMixin';
import formMeaningfulFieldsMixin from '@/mixins/formMeaningfulFieldsMixin';
import notifierMixin from '@/mixins/notifierMixin';
import { EditEmployeeBreakRequest } from '@/storage/gateways/AppointmentScheduleGateway';
import DatePickerDialog from '@/components/DatePickerDialog.vue';
import FormAppointmentTimeRange from '@/components/FormAppointmentTimeRange.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';

/**
 * @description Название события отмены перерыва.
 * @type {string}
 */
const NAME_EVENT_CANCEL_BREAK = 'cancel-break';

/**
 * @description Название события закрытия формы.
 * @type {string}
 */
const NAME_EVENT_CANCEL_FORM = 'cancel-form';

/**
 * @description Название события сохранения перерыва.
 * @type {string}
 */
const NAME_EVENT_STORE_BREAK = 'store-break';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Компонент формы изменения перерыва.
 */
export default {
  components: {
    TextareaLengthLimitation,
    DatePickerDialog,
    FormAppointmentTimeRange,
    PopoverMenu,
  },
  created() {
    this.onChangeMonth(this.yearAndMonth);
  },
  computed: {
    /**
     * @description Возможность редактирования даты и времени перерыва.
     * @type {boolean}
     */
    hasEditDateTime({ $options, initialDate, formatDateToISO }) {
      return formatDateToISO($options.today) <= initialDate;
    },
    /**
     * @description Заголовок.
     * @type {string}
     */
    dateAtTitle: ({ dateAt, getFullDate }) => getFullDate(dateAt),
    dateInputErrors: resolveInputErrors('date'),
    timeInputErrors: resolveInputErrors('time'),
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    isoDate: ({ dateAt, formatDateToISO }) => formatDateToISO(dateAt),
    /**
     * @description Год и месяц.
     * @type {string}
     */
    yearAndMonth: (vm) => {
      const date = new Date(vm.dateAt || vm.date);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      return String(year).concat('-', String(month).padStart(2, '0'));
    },
    /**
     * @description Значимые поля.
     * @type {any}
     */
    meaningfulFields: (vm) => ({
      dateAt: vm.dateAt,
      endsTime: vm.endsTime,
      note: vm.note,
      startsTime: vm.startsTime,
    }),
  },
  data() {
    return {
      dateAt: this.initialDate ? new Date(this.initialDate) : new Date(),
      endsTime: this.initialEndsTime || '00:00',
      note: this.initialNote || '',
      startsTime: this.initialStartsTime || '',
    };
  },
  today: new Date(),
  methods: {
    /**
     * @description Обработать отправку формы.
     * @returns {void}
     */
    onSubmit() {
      const request = new EditEmployeeBreakRequest(
        this.breakId,
        this.employee.id,
        this.isoDate,
        this.endsTime,
        this.startsTime,
        this.note,
      );
      this.$emit(NAME_EVENT_STORE_BREAK, request);
    },
    /**
     * @description Обработать изменение даты.
     * @param {Date} dateAt
     * @returns {void}
     */
    onInputDateAt(dateAt) {
      this.dateAt = dateAt;
      this.$emit('reload-timeline', this.employee.id, this.isoDate);
    },
    onSubmitTime(startsTime, endsTime, closePopover) {
      this.startsTime = startsTime;
      this.endsTime = endsTime;
      closePopover();
    },
    /**
     * @description Отправить событие закрытия формы.
     * @returns {void}
     */
    emitCloseForm() {
      this.$emit(NAME_EVENT_CANCEL_FORM);
    },
    /**
     * @description Отправить событие отмены перерыва сотрудника.
     * @returns {void}
     */
    emitCancelBreak() {
      this.$emit(NAME_EVENT_CANCEL_BREAK, this.breakId);
    },
    /**
     * @description Проверить является ли день рабочим.
     * @param {string} date Дата.
     * @returns {boolean}
     */
    checkDayIsWork(date) {
      return this.allowedDates.includes(date);
    },
    /**
     * @description Обработать изменение месяца.
     * @param {string} yearAndMonth Год и месяц.
     * @returns {void}
     */
    async onChangeMonth(yearAndMonth) {
      this.$emit('reload-allowed-dates', this.employee.id, yearAndMonth);
    },
  },
  mixins: [dateMixin, notifierMixin, formMeaningfulFieldsMixin],
  name: 'FormEmployeeBreakEdit',
  props: {
    /**
     * @description Рабочие дни.
     * @type {string[]}
     */
    allowedDates: { type: Array, required: true },
    /**
     * @description Идентификатор перерыва.
     * @type {number}
     */
    breakId: { type: Number, required: true },
    /**
     * @description Сотрудник.
     * @type {any}
     */
    employee: { type: Object, required: true },
    /**
     * @description Дата в формате ISO.
     * @type {string}
     */
    initialDate: { type: String, required: true },
    /**
     * @description Время окончания.
     * @type {string}
     */
    initialEndsTime: { type: String, required: true },
    /**
     * @description Примечание.
     * @type {string}
     */
    initialNote: { type: String, required: true },
    /**
     * @description Время начала.
     * @type {string}
     */
    initialStartsTime: { type: String, required: true },
    /**
     * @description Нарушение ограничений.
     * @type {any}
     */
    inputErrors: { type: Object, required: true },
    /**
     * @description Временной шаг.
     * @type {number}
     */
    timeline: { type: Object, required: true },
  },
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";

.form-employee-break-edit {
  flex-direction: column;
  display: flex;
  gap: 15px;
  &__employee {
    margin-top: 20px;
    font-weight: 500;
    font-size: 18px;
    line-height: 22px;
  }
  &__datetime {
    display: grid;
    grid-template-columns: repeat(2, fit-content(250px));
    column-gap: 20px;
    &__value {
      font-weight: 500;
      font-size: 18px;
      line-height: 22px;
      color: var(--action-color);
      border-bottom: 1px dashed;
      cursor: pointer;
      &:hover {
        border-bottom: 1px dashed transparent;
      }
      &.disabled {
        color: var(--reading-color);
        border-bottom: none;
        cursor: default;
        &:hover {
          border-bottom: none;
        }
      }
    }
    &__error {
      font-size: 12px;
      color: var(--warning-color);
    }
  }
  &__buttons {
    display: grid;
    gap: 10px;
    grid-template-columns: 1fr 1fr;
    margin: 64px 0 0 auto;
    max-width: 438px;
    width: 100%;
    &-submit {
      @include buttons.btn-blue(100%, 56px, 20px);
    }
    &-cancel {
      @include buttons.btn-transparent(100%, 56px, 20px);
      &.break {
        grid-column: 2;
      }
    }
  }
}
</style>
